import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import type { GameComponentProps } from '../core/types';
import { GridCell } from '../../shared/grid/GridCell';
import { HintButton } from '../../shared/grid/HintButton';
import { SymbolPad } from '../../shared/grid/SymbolPad';
import { UndoButton } from '../../shared/grid/UndoButton';
import { isGridArrowKey, moveGridSelection } from '../../shared/grid/navigation';
import { findPaireConflicts } from './logic';
import {
  isPairePuzzle,
  PAIRE_CELL_COUNT,
  PAIRE_SIZE,
  type PaireConstraint,
  type PairePuzzle,
  type PaireValue,
} from './types';

type PaireMove = { index: number; before: PaireValue; after: PaireValue; selectionBefore: number };
type PaireSavedState = {
  version: 1;
  board: PaireValue[];
  selectedIndex: number;
  history: PaireMove[];
  hintUsed: boolean;
  revealedWrong: number[];
};

function isPaireMove(value: unknown): value is PaireMove {
  if (!value || typeof value !== 'object') return false;
  const move = value as Partial<PaireMove>;
  return (
    Number.isInteger(move.index) &&
    move.index! >= 0 &&
    move.index! < PAIRE_CELL_COUNT &&
    (move.before === 0 || move.before === 1 || move.before === 2) &&
    (move.after === 0 || move.after === 1 || move.after === 2) &&
    Number.isInteger(move.selectionBefore) &&
    move.selectionBefore! >= 0 &&
    move.selectionBefore! < PAIRE_CELL_COUNT
  );
}

function readSavedState(value: unknown, puzzle: PairePuzzle): PaireSavedState {
  const firstEmpty = puzzle.puzzle.findIndex((cell) => cell === 0);
  const fallback: PaireSavedState = {
    version: 1,
    board: [...puzzle.puzzle],
    selectedIndex: firstEmpty >= 0 ? firstEmpty : 0,
    history: [],
    hintUsed: false,
    revealedWrong: [],
  };
  if (!value || typeof value !== 'object') return fallback;
  const candidate = value as Partial<PaireSavedState>;
  const board =
    Array.isArray(candidate.board) && candidate.board.length === PAIRE_CELL_COUNT
      ? candidate.board.map((cell, index) => {
          if (puzzle.puzzle[index] !== 0) return puzzle.puzzle[index];
          return cell === 1 || cell === 2 ? cell : 0;
        })
      : fallback.board;
  const selectedIndex =
    Number.isInteger(candidate.selectedIndex) &&
    candidate.selectedIndex! >= 0 &&
    candidate.selectedIndex! < PAIRE_CELL_COUNT
      ? candidate.selectedIndex!
      : fallback.selectedIndex;
  const history = Array.isArray(candidate.history)
    ? candidate.history.filter(isPaireMove).filter((move) => puzzle.puzzle[move.index] === 0)
    : [];
  const revealedWrong = Array.isArray(candidate.revealedWrong)
    ? candidate.revealedWrong.filter(
        (index): index is number =>
          Number.isInteger(index) &&
          index >= 0 &&
          index < PAIRE_CELL_COUNT &&
          puzzle.puzzle[index] === 0 &&
          board[index] !== 0,
      )
    : [];
  return {
    version: 1,
    board,
    selectedIndex,
    history,
    hintUsed: candidate.hintUsed === true || revealedWrong.length > 0,
    revealedWrong,
  };
}

function constraintStyle(constraint: PaireConstraint): CSSProperties {
  const rowA = Math.floor(constraint.a / PAIRE_SIZE);
  const rowB = Math.floor(constraint.b / PAIRE_SIZE);
  const columnA = constraint.a % PAIRE_SIZE;
  const columnB = constraint.b % PAIRE_SIZE;
  if (rowA === rowB) {
    return {
      left: `${(Math.max(columnA, columnB) / PAIRE_SIZE) * 100}%`,
      top: `${((rowA + 0.5) / PAIRE_SIZE) * 100}%`,
    };
  }
  return {
    left: `${((columnA + 0.5) / PAIRE_SIZE) * 100}%`,
    top: `${(Math.max(rowA, rowB) / PAIRE_SIZE) * 100}%`,
  };
}

function constraintDescription(constraint: PaireConstraint): string {
  const rowA = Math.floor(constraint.a / PAIRE_SIZE) + 1;
  const columnA = (constraint.a % PAIRE_SIZE) + 1;
  const rowB = Math.floor(constraint.b / PAIRE_SIZE) + 1;
  const columnB = (constraint.b % PAIRE_SIZE) + 1;
  return `Row ${rowA} column ${columnA} and row ${rowB} column ${columnB} must ${
    constraint.kind === 'eq' ? 'match' : 'differ'
  }`;
}

export function PaireGame({
  puzzle,
  savedState,
  disabled,
  onStateChange,
  onCharge,
  onComplete,
}: GameComponentProps) {
  if (!isPairePuzzle(puzzle.payload)) throw new Error('Paire received an invalid puzzle payload.');
  const pairePuzzle = puzzle.payload;
  const initial = useMemo(() => readSavedState(savedState, pairePuzzle), [savedState, pairePuzzle]);
  const [board, setBoard] = useState(initial.board);
  const [selectedIndex, setSelectedIndex] = useState(initial.selectedIndex);
  const [history, setHistory] = useState(initial.history);
  const [hintUsed, setHintUsed] = useState(initial.hintUsed);
  const [revealedWrong, setRevealedWrong] = useState(new Set(initial.revealedWrong));
  const [status, setStatus] = useState('');
  const gridRef = useRef<HTMLDivElement>(null);
  const completionSent = useRef(false);
  const conflicts = useMemo(
    () => findPaireConflicts(board, pairePuzzle.constraints),
    [board, pairePuzzle.constraints],
  );

  const persist = useCallback(
    (
      nextBoard: PaireValue[],
      nextSelected: number,
      nextHistory: PaireMove[],
      nextHintUsed: boolean,
      nextWrong: Set<number>,
    ) => {
      onStateChange({
        version: 1,
        board: nextBoard,
        selectedIndex: nextSelected,
        history: nextHistory,
        hintUsed: nextHintUsed,
        revealedWrong: [...nextWrong],
      });
    },
    [onStateChange],
  );

  const select = useCallback(
    (index: number, focus = false) => {
      setSelectedIndex(index);
      persist(board, index, history, hintUsed, revealedWrong);
      if (focus) {
        window.requestAnimationFrame(() => {
          gridRef.current?.querySelector<HTMLElement>(`[data-grid-index="${index}"]`)?.focus();
        });
      }
    },
    [board, hintUsed, history, persist, revealedWrong],
  );

  const setCell = useCallback(
    (index: number, value: PaireValue) => {
      if (disabled || pairePuzzle.puzzle[index] !== 0 || board[index] === value) return;
      const nextBoard = [...board];
      const move: PaireMove = {
        index,
        before: board[index],
        after: value,
        selectionBefore: selectedIndex,
      };
      nextBoard[index] = value;
      const nextHistory = [...history, move];
      const nextWrong = new Set(revealedWrong);
      nextWrong.delete(index);
      setBoard(nextBoard);
      setSelectedIndex(index);
      setHistory(nextHistory);
      setRevealedWrong(nextWrong);
      setStatus(value === 1 ? 'Star placed' : value === 2 ? 'Circle placed' : 'Cell cleared');
      persist(nextBoard, index, nextHistory, hintUsed, nextWrong);
    },
    [board, disabled, hintUsed, history, pairePuzzle.puzzle, persist, revealedWrong, selectedIndex],
  );

  const cycle = useCallback(
    (index: number) => setCell(index, board[index] === 0 ? 1 : board[index] === 1 ? 2 : 0),
    [board, setCell],
  );

  const undo = useCallback(() => {
    if (disabled || history.length === 0) return;
    const move = history.at(-1)!;
    const nextHistory = history.slice(0, -1);
    const nextBoard = [...board];
    nextBoard[move.index] = move.before;
    const nextWrong = new Set(revealedWrong);
    nextWrong.delete(move.index);
    setBoard(nextBoard);
    setSelectedIndex(move.selectionBefore);
    setHistory(nextHistory);
    setRevealedWrong(nextWrong);
    setStatus('Move undone');
    persist(nextBoard, move.selectionBefore, nextHistory, hintUsed, nextWrong);
  }, [board, disabled, hintUsed, history, persist, revealedWrong]);

  const revealWrong = useCallback(() => {
    if (disabled) return;
    const nextWrong = new Set<number>();
    board.forEach((value, index) => {
      if (pairePuzzle.puzzle[index] === 0 && value !== 0 && value !== pairePuzzle.solution[index]) {
        nextWrong.add(index);
      }
    });
    if (!hintUsed) onCharge('reveal-wrong', 30_000, 'Reveal wrong Paire cells');
    setHintUsed(true);
    setRevealedWrong(nextWrong);
    setStatus(
      `${nextWrong.size} wrong ${nextWrong.size === 1 ? 'cell' : 'cells'} found${hintUsed ? '' : ' · +30s'}`,
    );
    persist(board, selectedIndex, history, true, nextWrong);
  }, [board, disabled, hintUsed, history, onCharge, pairePuzzle, persist, selectedIndex]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (disabled || event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key === '1' || event.key.toLowerCase() === 's') {
        event.preventDefault();
        setCell(selectedIndex, 1);
      } else if (event.key === '2' || event.key.toLowerCase() === 'c') {
        event.preventDefault();
        setCell(selectedIndex, 2);
      } else if (event.key === 'Backspace' || event.key === 'Delete') {
        event.preventDefault();
        setCell(selectedIndex, 0);
      } else if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        cycle(selectedIndex);
      } else if (isGridArrowKey(event.key)) {
        event.preventDefault();
        select(moveGridSelection(selectedIndex, event.key, PAIRE_SIZE, PAIRE_SIZE), true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [cycle, disabled, select, selectedIndex, setCell]);

  useEffect(() => {
    if (
      completionSent.current ||
      !board.every((value, index) => value === pairePuzzle.solution[index])
    ) {
      return;
    }
    completionSent.current = true;
    onComplete({
      kind: 'solved',
      baseDeltaMs: hintUsed ? 0 : -10_000,
      message: 'Perfect pairings!',
      metadata: { hintUsed, moveCount: history.length },
    });
  }, [board, hintUsed, history.length, onComplete, pairePuzzle.solution]);

  return (
    <section className="logic-grid-game paire-game" aria-labelledby="paire-title">
      <p className="eyebrow">Three stars · three circles · never three together</p>
      <h1 id="paire-title" className="sr-only">
        Paire
      </h1>
      <div className="paire-grid-wrap">
        <div
          ref={gridRef}
          className="logic-grid paire-grid"
          role="grid"
          aria-label="Paire grid, 6 rows by 6 columns"
        >
          {board.map((value, index) => {
            const row = Math.floor(index / PAIRE_SIZE);
            const column = index % PAIRE_SIZE;
            const given = pairePuzzle.puzzle[index] !== 0;
            const conflict = conflicts.has(index);
            const wrong = revealedWrong.has(index);
            const state = value === 1 ? 'star' : value === 2 ? 'blue circle' : 'empty';
            return (
              <GridCell
                key={index}
                index={index}
                label={`Row ${row + 1} column ${column + 1}, ${state}, ${given ? 'given' : 'editable'}${
                  conflict ? ', conflict' : ''
                }${wrong ? ', revealed wrong' : ''}`}
                selected={selectedIndex === index}
                given={given}
                conflict={conflict}
                wrong={wrong}
                className={`logic-cell paire-cell paire-cell--value-${value}`}
                onSelect={select}
                onActivate={cycle}
                onDirectSelect={(cellIndex) => setCell(cellIndex, 2)}
              >
                <span aria-hidden="true">{value === 1 ? '★' : value === 2 ? '●' : ''}</span>
                {(conflict || wrong) && <i aria-hidden="true">!</i>}
              </GridCell>
            );
          })}
        </div>
        {pairePuzzle.constraints.map((constraint) => (
          <span
            key={`${constraint.a}-${constraint.b}`}
            className="paire-constraint"
            data-constraint-a={constraint.a}
            data-constraint-b={constraint.b}
            style={constraintStyle(constraint)}
            aria-hidden="true"
          >
            {constraint.kind === 'eq' ? '=' : '×'}
          </span>
        ))}
      </div>
      <ul className="sr-only" aria-label="Paire pair constraints">
        {pairePuzzle.constraints.map((constraint) => (
          <li key={`${constraint.a}-${constraint.b}`}>{constraintDescription(constraint)}</li>
        ))}
      </ul>
      <div className="logic-legend" aria-label="Paire controls legend">
        ★ star: one tap · <span className="paire-circle">●</span> circle: two taps or right-click ·
        = match · × differ
      </div>
      <div className="sudoku-status" role="status" aria-live="polite">
        {status}
      </div>
      <SymbolPad
        options={[
          { value: 1, symbol: '★', label: 'star' },
          { value: 2, symbol: '●', label: 'blue circle' },
        ]}
        disabled={disabled || pairePuzzle.puzzle[selectedIndex] !== 0}
        onValue={(value) => setCell(selectedIndex, value as PaireValue)}
        onErase={() => setCell(selectedIndex, 0)}
      />
      <div className="grid-actions">
        <UndoButton count={history.length} disabled={disabled} onUndo={undo} />
        <HintButton
          label="Reveal wrong"
          costSeconds={30}
          paid={hintUsed}
          disabled={disabled}
          onClick={revealWrong}
        />
      </div>
    </section>
  );
}
